import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./StockItem.module.css";
import { enhancedStockInfo } from "../../utils/mockStockData";

import ItemImage from "Shared/ItemImage";
import ItemIconSection from "Shared/ItemIconSection";
import CartEditModal from "components/CartEditModal";
import { useAddToCart } from "../../hooks/useCart";
import AddedPopup from "components/AddedPopup";
import ItemNote from "components/ItemNote";
import { lowStockMessage } from "../../utils/stockStatus";
import { trackProductAdded } from "../../utils/analytics";
import { getPriceLabel } from "../../utils/getPriceLabel";
import { getSwatchStyle } from "../../utils/swatchStyle";
import { resolveVariantColorAndSize } from "../../utils/variantParsing";
import {
  formatRetailPrice,
  RETAIL_PRICE_LABEL,
} from "../../utils/formatRetailPrice";

import HotBadge from "assets/HotBadge.svg";
import NewBadge from "assets/NewBadge.svg";
import PreOrderIcon from "assets/PreOrder.svg";
import ThirdPartyIcon from "assets/3rdPartyIcon.svg";

export function StockItem({
  stockInfo,
  onClick,
  onDelete,
  showNote,
  forceListLayout,
  removeFromListOnAddToCart,
}) {
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const [isCartAdded, setIsCartAdded] = useState(false);
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const addToCart = useAddToCart();

  useEffect(() => {
    if (isCartAdded) {
      const timer = setTimeout(() => setIsCartAdded(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isCartAdded]);
  const authorName = stockInfo.author || stockInfo.creator;
  const retailPriceText = formatRetailPrice(stockInfo.retailPrice);
  const isConfigurable =
    String(stockInfo?.itemType || "")
      .trim()
      .toUpperCase() === "CONFIGURABLE";

  const activate = () => onClick && onClick();
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      activate();
    }
  };

  const enhanced = enhancedStockInfo.find(
    (i) => String(i.stockId) === String(stockInfo.stockId)
  );

  const colorChoices = useMemo(() => {
    if (!isConfigurable) {
      return [];
    }

    if (
      Array.isArray(stockInfo?.detailColors) &&
      stockInfo.detailColors.length > 0
    ) {
      return stockInfo.detailColors
        .map((option) => {
          if (typeof option === "string") {
            return { label: option, imageUrl: stockInfo?.imageUrl || null };
          }

          return {
            label: option?.label || option?.value || "",
            imageUrl: option?.imageUrl || stockInfo?.imageUrl || null,
            swatchHex: option?.swatchHex || null,
            swatchImage: option?.swatchImage || null,
          };
        })
        .filter((option) => option.label);
    }

    if (Array.isArray(enhanced?.images) && enhanced.images.length > 0) {
      return enhanced.images
        .map((option, index) => ({
          label: option?.label || option?.name || `Option ${index + 1}`,
          imageUrl: option?.src || stockInfo?.imageUrl || null,
        }))
        .filter((option) => option.label);
    }

    return [];
  }, [
    isConfigurable,
    stockInfo?.detailColors,
    stockInfo?.imageUrl,
    enhanced?.images,
  ]);

  const selectedColor =
    colorChoices.find(
      (option) =>
        String(option?.label || "").toLowerCase() ===
        String(stockInfo?.color || "").toLowerCase()
    ) ||
    colorChoices[0] ||
    null;

  // List cards show the thumbnail upload, falling back to the full-size
  // image (and finally "") if the product only has one of the two.
  const selectedImage = stockInfo?.thumbUrl || stockInfo?.imageUrl || "";

  // Format variant display name if item has a variantId (from list)
  const variantInfo = React.useMemo(() => {
    if (!stockInfo?.variantId || !Array.isArray(stockInfo?.variants)) {
      return null;
    }

    const selectedVariant = stockInfo.variants.find(
      (v) => String(v?.variantId) === String(stockInfo.variantId)
    );

    if (!selectedVariant?.displayName) {
      return null;
    }

    return resolveVariantColorAndSize(selectedVariant);
  }, [stockInfo?.variantId, stockInfo?.variants]);

  const swatchColorForLabel = (label, index) => {
    const raw = String(label || "").trim();
    if (!raw) return "#92b7bc";

    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(raw)) {
      return raw;
    }

    const token = raw.toLowerCase();

    // Some catalog labels are combo names (e.g. "Blue/Gray") while design
    // expects four distinct bubble colors for the row. Use ordered fallback.
    if (token.includes("/") && colorChoices.length === 4) {
      const figmaPalette = ["#000000", "#92B7BC", "#5F86D6", "#FFFFFF"];
      return figmaPalette[index] || "#92b7bc";
    }

    const colorMap = {
      black: "#1e1e1e",
      white: "#f8f8f8",
      grey: "#9fa4a8",
      gray: "#9fa4a8",
      blue: "#5f86d6",
      teal: "#4fa6ad",
      navy: "#243746",
      red: "#cf4e4e",
      green: "#4f9d58",
      brown: "#8b6a4f",
      tan: "#ccb08a",
    };

    const match = Object.keys(colorMap).find((name) => token.includes(name));
    return match ? colorMap[match] : "#92b7bc";
  };

  const handleAddToCart = (updatedItem = {}) => {
    const rawProductId = stockInfo?.productId ?? stockInfo?.stockId;
    const parsedProductId = Number(rawProductId);
    const productId = Number.isNaN(parsedProductId)
      ? rawProductId
      : parsedProductId;
    const color =
      updatedItem?.color ?? selectedColor?.label ?? stockInfo?.color;
    const size = updatedItem?.size ?? stockInfo?.size ?? null;
    const quantity = updatedItem?.quantity ?? stockInfo?.quantity ?? 1;

    addToCart.mutate(
      {
        productId,
        variationId: updatedItem?.variationId ?? stockInfo?.variationId ?? null,
        variantId: updatedItem?.variantId ?? stockInfo?.variantId ?? null,
        color,
        size,
        quantity,
      },
      {
        // Only show the notification once the mutation actually succeeds.
        onSuccess: () => {
          setIsCartAdded(true);
          trackProductAdded(stockInfo, { quantity, color, size });

          // Saved for Later is a "move to cart" action: once the item is
          // confirmed in the cart, it shouldn't remain in that list.
          if (removeFromListOnAddToCart && onDelete) {
            const listProductId = stockInfo?.listProductId ?? stockInfo?.id;
            if (listProductId != null) {
              onDelete(listProductId);
            }
          }
        },
      }
    );
  };

  return (
    <div className={styles.stockItem}>
      <div className={styles.itemRow}>
        {/* IMAGE ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â clickable */}
        <div
          className={styles.image}
          role="button"
          tabIndex={0}
          aria-label={`View details for ${stockInfo.title}`}
          onClick={activate}
          onKeyDown={handleKeyDown}
        >
          <ItemImage
            imageSrc={selectedImage}
            alt={`${stockInfo.title} Image`}
            isThumb={true}
          />
        </div>

        <div className={styles.content}>
          <div className={styles.details}>
            {/* TITLE ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â also clickable */}
            <div
              className={styles.title}
              role="button"
              tabIndex={0}
              onClick={activate}
              onKeyDown={handleKeyDown}
              aria-label={`Open ${stockInfo.title} details`}
            >
              {stockInfo.title}
            </div>

            <div className={styles.costLabel}>
              {getPriceLabel(stockInfo?.itemType)}:
              <span className={styles.cost}>
                {" "}
                {stockInfo.memberPrice != null
                  ? `$${stockInfo.memberPrice.toFixed(2)}`
                  : stockInfo.retailPrice != null
                  ? `$${stockInfo.retailPrice.toFixed(2)}`
                  : "N/A"}{" "}
              </span>
            </div>

            {retailPriceText && (
              <div className={styles.costLabel}>
                {RETAIL_PRICE_LABEL}:
                <span className={styles.cost}> {retailPriceText}</span>
              </div>
            )}

            {authorName && (
              <div className={styles.author}>
                <span className={styles.authorLabel}>Author:</span> {authorName}
              </div>
            )}

            {variantInfo && (
              <div className={styles.variantLabel}>
                <span className={styles.variantLabelKey}>Color:</span>
                <span className={styles.variantLabelValue}>
                  {variantInfo.color}
                </span>
                <span className={styles.variantLabelKey}>Size:</span>
                <span className={styles.variantLabelValue}>
                  {variantInfo.size}
                </span>
              </div>
            )}

            {stockInfo.subTitle && !stockInfo.isLowStock && (
              <div className={styles.subTitle}>{stockInfo.subTitle}</div>
            )}

            {colorChoices.length > 0 && (
              <div className={styles.colorOptions}>
                {colorChoices.map((option, index) => {
                  return (
                    <span
                      key={`${option.label}-${index}`}
                      className={styles.colorButton}
                      aria-label={option.label}
                      role="img"
                      title={option.label}
                      style={
                        getSwatchStyle(option) || {
                          backgroundColor: swatchColorForLabel(
                            option.label,
                            index
                          ),
                        }
                      }
                    ></span>
                  );
                })}
              </div>
            )}

            <div className={styles.icons}>
              <ItemIconSection
                item={{
                  stockId: stockInfo.stockId, // ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ needed for enhancedStockInfo lookup
                  listProductId: stockInfo.listProductId,
                  sortOrder: stockInfo.sortOrder,
                  id: stockInfo.id,
                  title: stockInfo.title,
                  itemType: stockInfo.itemType,
                  category: stockInfo.category,
                  memberPrice: stockInfo.memberPrice,
                  author: authorName,
                  isReadable: stockInfo.isReadable,
                  isUnavailable: stockInfo.isUnavailable,
                  quantity: stockInfo.quantity ?? 1,
                  color: selectedColor?.label ?? stockInfo?.color,
                  size: stockInfo.size,
                  image: selectedImage,
                  imageUrl: selectedImage,
                  variationId: stockInfo?.variationId,
                  variantId: stockInfo?.variantId,
                  isOutOfStock: stockInfo.isOutOfStock,
                  isLowStock: stockInfo.isLowStock,
                  quantityAvailable: stockInfo.quantityAvailable,
                  isPreOrder: stockInfo.isPreOrder,
                  isLiked: stockInfo.isLiked,
                  isInList: stockInfo.isInList,
                  variants: stockInfo.variants,
                  detailColors: stockInfo.detailColors,
                  variantMatrix: stockInfo.variantMatrix,
                }}
                isItemDetails={false}
                hideCart={stockInfo.isPreOrder}
                disableCart={stockInfo.isOutOfStock}
                hideOutOfStockLabel
                hideLowStockLabel
                onCartClick={handleAddToCart}
                onNoteClick={
                  showNote ? () => setIsNoteOpen((v) => !v) : undefined
                }
                hideDelete={!onDelete}
                onDelete={onDelete}
                forceListLayout={forceListLayout}
                onClick={(e) => {
                  e?.stopPropagation?.();
                }}
              />

              {stockInfo.isPreOrder && (
                <button
                  type="button"
                  className={styles.preOrderBtn}
                  aria-label="Pre-order this item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCartModalOpen(true);
                  }}
                >
                  <img src={PreOrderIcon} alt="Pre-order item" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={styles.column}>
          <div className={styles.badges}>
            {stockInfo.is3rdParty && (
              <img src={ThirdPartyIcon} alt="3rd party item" />
            )}
            {stockInfo.isHot && <img src={HotBadge} alt="Hot item" />}
            {stockInfo.isNew && <img src={NewBadge} alt="New item" />}
          </div>
          {stockInfo.isOutOfStock ? (
            <div className={styles.outOfStockCorner}>Out of Stock</div>
          ) : (
            stockInfo.isLowStock && (
              <div className={styles.lowStockCorner}>
                {lowStockMessage(stockInfo.quantityAvailable)}
              </div>
            )
          )}
        </div>
      </div>

      <CartEditModal
        isOpen={isCartModalOpen}
        onClose={() => setIsCartModalOpen(false)}
        onCancel={() => setIsCartModalOpen(false)}
        onSave={(updatedItem) => {
          handleAddToCart(updatedItem);
          setIsCartModalOpen(false);
        }}
        item={{
          title: stockInfo.title,
          image: selectedImage,
          isPreOrder: true,
          size: stockInfo.size,
          color: selectedColor?.label ?? stockInfo?.color,
        }}
        colorOptions={
          colorChoices.length > 0
            ? colorChoices.map((option) => ({
                label: option.label,
                imageUrl: option.imageUrl,
              }))
            : undefined
        }
      />

      {showNote && (
        <ItemNote
          isEditing={isNoteOpen}
          stockId={stockInfo.stockId}
          onClose={() => setIsNoteOpen(false)}
        />
      )}

      <AddedPopup variants="cart" isAdded={isCartAdded} />
    </div>
  );
}

StockItem.propTypes = {
  stockInfo: PropTypes.shape({
    stockId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    listProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    variationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    variantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sortOrder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.number,
    imageUrl: PropTypes.string,
    title: PropTypes.string,
    author: PropTypes.string,
    creator: PropTypes.string,
    category: PropTypes.string,
    sku: PropTypes.string,
    is3rdParty: PropTypes.bool,
    subTitle: PropTypes.string,
    itemType: PropTypes.string,
    memberPrice: PropTypes.number,
    quantity: PropTypes.number,
    size: PropTypes.string,
    isHot: PropTypes.bool,
    isNew: PropTypes.bool,
    hasColorChoices: PropTypes.bool,
    colorOptions: PropTypes.arrayOf(PropTypes.string),
    isPreOrder: PropTypes.bool,
    isOutOfStock: PropTypes.bool,
    isLowStock: PropTypes.bool,
    quantityAvailable: PropTypes.number,
    isUnavailable: PropTypes.bool,
  }),
  onClick: PropTypes.func,
  onDelete: PropTypes.func,
  showNote: PropTypes.bool,
  forceListLayout: PropTypes.bool,
  removeFromListOnAddToCart: PropTypes.bool,
};

export default React.memo(StockItem);
